import type { SettingsPage } from "../types";
import { CURSOR_COLORS } from "../../collab/cursorColors";

const CURSOR_COLOR_NAMES: Record<string, string> = {
  "#ef4444": "Red",
  "#f59e0b": "Amber",
  "#10b981": "Green",
  "#3b82f6": "Blue",
  "#8b5cf6": "Violet",
  "#ec4899": "Pink",
  "#14b8a6": "Teal",
  "#f97316": "Orange",
};

const collaborationSettings: SettingsPage = {
  id: "collaboration",
  label: "Collaboration",
  order: 3,
  items: [
    {
      key: "webrtcHeading",
      label: "WebRTC settings",
      type: "heading",
      default: "",
    },
    {
      key: "useBundledServer",
      label: "Use built-in server (My WebRTC)",
      type: "toggle",
      default: true,
      description:
        "Use the app's built-in signaling server so real-time co-editing works out of " +
        "the box. This is a best-effort convenience and may change — as usage grows it " +
        "could become a paid tier or be discontinued for cost/latency reasons. Running " +
        "your own server is recommended for anything long-term (see " +
        "docs/y-webrtc-oracle-setup.md in the y-webrtc repo). Turn this off to use your " +
        "own signaling server, configured below.",
    },
    {
      key: "signalingUrl",
      label: "Signaling server URL",
      type: "text",
      default: "",
      description:
        "WebSocket URL of your y-webrtc signaling server (e.g. wss://your-host:4444). " +
        "Comma-separate a primary and a local fallback to try both. Enables real-time " +
        "co-editing with anyone who opens the same project (same password) and document. " +
        "Only WebRTC connection metadata passes through this server; document content is " +
        "peer-to-peer and end-to-end encrypted with your project password.",
      visibleWhen: { key: "useBundledServer", equals: false },
    },
    {
      key: "signalingToken",
      label: "Signaling server token",
      type: "password",
      default: "",
      description:
        "The app token your signaling server was started with (SIGNALING_TOKEN). The " +
        "app exchanges it for a short-lived access key before connecting. Leave blank if " +
        "your server has no token (plain/upstream y-webrtc) — the app then connects " +
        "directly without registering.",
      visibleWhen: { key: "useBundledServer", equals: false },
    },
    {
      key: "turnHeading",
      label: "TURN settings",
      type: "heading",
      default: "",
    },
    {
      key: "useTurn",
      label: "Use a TURN relay server",
      type: "toggle",
      default: false,
      description:
        "Turn this on if real-time collaboration doesn't sync reliably on some networks " +
        "— especially mobile/cellular. The default setup only uses public STUN servers, " +
        "which often can't establish a direct connection through the NAT/firewall on " +
        "cellular networks; a TURN server relays the connection instead, so it still " +
        "works. For it to help, every collaborator whose network is restrictive should " +
        "configure the same TURN server below.",
    },
    {
      key: "turnUrl",
      label: "TURN server URL(s)",
      type: "text",
      default: "",
      description:
        "A TURN relay server (e.g. turn:host:3478 or turns:host:5349 for TLS). " +
        "Comma-separate multiple URLs to offer more than one transport (e.g. UDP and " +
        "TCP/TLS). Get one by self-hosting (e.g. coturn) or from a third-party TURN " +
        "provider.",
      visibleWhen: { key: "useTurn", equals: true },
    },
    {
      key: "turnUsername",
      label: "TURN username",
      type: "text",
      default: "",
      description: "Username for the TURN server above, if it requires authentication.",
      visibleWhen: { key: "useTurn", equals: true },
    },
    {
      key: "turnCredential",
      label: "TURN credential",
      type: "password",
      default: "",
      description: "Password/credential for the TURN server above.",
      visibleWhen: { key: "useTurn", equals: true },
    },
    {
      key: "presenceHeading",
      label: "Presence",
      type: "heading",
      default: "",
    },
    {
      key: "displayName",
      label: "Your name",
      type: "text",
      default: "",
      description:
        "Shown on your cursor to other collaborators. Defaults to a random guest name.",
    },
    {
      key: "cursorColor",
      label: "Your cursor color",
      type: "select",
      default: "random",
      options: [
        { value: "random", label: "Random (changes each session)" },
        ...CURSOR_COLORS.map((hex) => ({ value: hex, label: CURSOR_COLOR_NAMES[hex] ?? hex })),
      ],
      description: "Pick a fixed color so collaborators recognize your cursor across sessions.",
    },
    {
      key: "advancedHeading",
      label: "Advanced",
      type: "heading",
      default: "",
    },
    {
      key: "stunUrls",
      label: "STUN server URL(s)",
      type: "text",
      default: "",
      description:
        "Override the default public STUN servers (Google/Twilio) used alongside your " +
        "TURN server above. Comma-separate multiple. Leave blank to use the defaults.",
      visibleWhen: { key: "useTurn", equals: true },
    },
    {
      key: "signalingReconnectSec",
      label: "Signaling reconnect timeout (seconds)",
      type: "number",
      default: 12,
      min: 5,
      max: 120,
      description: "How long to wait for the signaling server to connect before retrying with a fresh key.",
    },
    {
      key: "peerStallThresholdSec",
      label: "Peer stall warning threshold (seconds)",
      type: "number",
      default: 15,
      min: 10,
      max: 120,
      description:
        "How long a collaborator can be nearby without a live connection before the " +
        "\"connection couldn't be established\" warning appears. Kept at 10s or above so " +
        "it never fires before the app's own connection attempt has had a chance to finish.",
    },
  ],
};

export default collaborationSettings;
